/**
 * Catalog loading.
 *
 * Fetched once at startup from `/data/locali.json`, which is no longer a file:
 * the Worker builds it from the database and holds it at the edge for half a
 * minute. The address stayed the same on purpose — the app never needed to
 * know where the catalogue came from, and now that it comes from a database an
 * approved correction reaches this fetch within that half minute.
 *
 * Validation stays even though the panel now writes these rows. A schema
 * constrains types, not sense, and one bad row must still not blank the page.
 */

const CATALOG_URL = '/data/locali.json';
const PROGRAMS_URL = '/data/programmi.json';

/** The only three values the `stato` field may hold. */
export const STATUSES = ['aperto', 'chiuso', 'chiuso_temporaneamente', 'ignoto'];

/** Used whenever a venue's status has not been verified. Never guessed. */
export const DEFAULT_STATUS = 'ignoto';

/**
 * Who established the status. A date on its own cannot say whether a status
 * came from someone checking sources or from a visitor reporting what they
 * found, and those are worth different amounts.
 */
/**
 * Who last checked a status. `mappe` is a maps provider answering through
 * the panel: worth telling apart from a person, because it is only ever as
 * good as the place it matched.
 */
export const VERIFIERS = ['ricerca', 'segnalazione', 'mappe'];

/** Rome sits well inside these; anything outside is a typo, not a venue. */
const ITALY_BOUNDS = { minLat: 35.0, maxLat: 47.5, minLng: 6.0, maxLng: 19.0 };

/**
 * The address to ask for, under the version of the data the page was built
 * from (the Worker writes it into the head). A new address for every change:
 * a copy kept anywhere along the way — Google's renderer keeps them for days —
 * can never be older than the page. Without the tag, the bare address.
 */
function versioned(url) {
  const version = document.querySelector('meta[name="dati-versione"]')?.content;
  return version ? `${url}?v=${encodeURIComponent(version)}` : url;
}

async function fetchJson(url) {
  const response = await fetch(versioned(url), { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`${url} — HTTP ${response.status} ${response.statusText}`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error(`${url} — expected a JSON array`);
  }
  return payload;
}

/**
 * Checks one venue against the schema and returns a normalised copy.
 *
 * The catalogue is hand-edited by a single maintainer, so problems are
 * collected and surfaced rather than thrown: one bad row must not blank the
 * whole page. Anything unverifiable falls back to the safe value.
 */
function normaliseVenue(venue, index, programIds, issues) {
  const where = venue?.id ? `locale "${venue.id}"` : `locale #${index + 1}`;
  const report = (message) => issues.push(`${where}: ${message}`);

  if (!venue || typeof venue !== 'object') {
    report('non è un oggetto, ignorato');
    return null;
  }
  if (!venue.id) report('manca il campo id');
  if (!venue.nome) report('manca il campo nome');
  if (!programIds.has(venue.programma)) {
    report(`programma sconosciuto "${venue.programma}"`);
  }

  const lat = Number(venue.lat);
  const lng = Number(venue.lng);
  const hasCoords =
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= ITALY_BOUNDS.minLat &&
    lat <= ITALY_BOUNDS.maxLat &&
    lng >= ITALY_BOUNDS.minLng &&
    lng <= ITALY_BOUNDS.maxLng;
  // Not reported for the guide: its restaurants wait for the pass on the map,
  // and four hundred lines of the same news would bury the ones worth reading.
  if (!hasCoords && venue.programma !== 'stellati-michelin') {
    // Kept in the list anyway — it just cannot be put on the map later.
    report('coordinate mancanti o fuori dall\u2019Italia');
  }

  let stato = venue.stato ?? DEFAULT_STATUS;
  if (!STATUSES.includes(stato)) {
    report(`stato non valido "${venue.stato}", trattato come "${DEFAULT_STATUS}"`);
    stato = DEFAULT_STATUS;
  }

  let verifier = venue.verificato_da ?? null;
  if (verifier !== null && !VERIFIERS.includes(verifier)) {
    report(`verificato_da non valido "${venue.verificato_da}"`);
    verifier = null;
  }
  if (venue.verificato_il && !verifier) {
    report('verificato_il presente ma verificato_da mancante');
  }

  const posizione =
    venue.posizione === null || venue.posizione === undefined
      ? null
      : Number(venue.posizione);
  if (posizione !== null && !Number.isInteger(posizione)) {
    report(`posizione non valida "${venue.posizione}"`);
  }
  if (venue.vincitore === true && posizione !== null && posizione !== 1) {
    report(`marcato vincitore ma con posizione ${posizione}`);
  }

  return {
    ...venue,
    lat: hasCoords ? lat : null,
    lng: hasCoords ? lng : null,
    stato,
    posizione: Number.isInteger(posizione) ? posizione : null,
    vincitore: venue.vincitore === true,
    fonte: venue.fonte ?? null,
    // The name the place had when it was on television, when it no longer goes
    // by it. A viewer looking for somewhere they saw on TV types the name they
    // heard, and a catalogue of TV appearances that cannot answer that has
    // missed its own point.
    nome_in_onda: venue.nome_in_onda ?? null,
    // Optional, and null on every venue so far: nothing is borrowed from
    // anywhere to fill it, and the cards simply go without.
    foto: venue.foto ?? null,
    // The street address, and whether anybody has actually checked it. These
    // are two facts, not one: most pins here were geocoded from a name and
    // land in the middle of a street, which is a fine dot and a bad
    // destination. Only the confirmed ones may be handed to a navigator.
    indirizzo: venue.indirizzo ?? null,
    indirizzo_confermato: venue.indirizzo_confermato === true,
    // From the venue's own listing on the map, when there is one. Unlike the
    // address it carries no confirmed flag: a wrong number is a wasted call,
    // not somebody driven to the wrong town.
    telefono: venue.telefono ?? null,
    // The venue's own listing on Google Maps, when a lookup matched it for
    // certain. Without it the card's button searches the confirmed address.
    google_id: venue.google_id ?? null,
    // A status with no date ages in silence: in a year an unchecked "aperto"
    // would be indistinguishable from one confirmed this morning.
    verificato_il: venue.verificato_il ?? null,
    verificato_da: verifier,
    puntata: {
      titolo: venue.puntata?.titolo ?? null,
      data: venue.puntata?.data ?? null,
      precisione: venue.puntata?.precisione ?? null,
    },
  };
}

/**
 * Downloads the whole catalogue. Both files are fetched in parallel: the
 * programme list is needed to resolve and validate `programma` references.
 */
export async function loadCatalog() {
  const [rawVenues, programs] = await Promise.all([
    fetchJson(CATALOG_URL),
    fetchJson(PROGRAMS_URL),
  ]);

  const programIds = new Set(programs.map((program) => program.id));
  const issues = [];
  const venues = rawVenues
    .map((venue, index) => normaliseVenue(venue, index, programIds, issues))
    .filter(Boolean);

  const seen = new Set();
  for (const venue of venues) {
    if (seen.has(venue.id)) issues.push(`id duplicato "${venue.id}"`);
    seen.add(venue.id);
  }

  return { venues, programs, issues };
}
